/**
 * The three actions that change a role's calendar holds.
 *
 * Split out of useBoardActions to keep that file inside its line budget, and
 * because these are the only handlers that touch hold state rather than the
 * board snapshot or the tracking map.
 *
 * Every call applies the hold list the server returned. Re-reading it separately
 * could disagree with what just happened, and the recruiter RELEASES from this
 * list — a stale row would delete the wrong slot's calendar entry.
 */
import type { Dispatch, SetStateAction } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type {
  GradedSlot,
  RoleBoard,
  SchedulingCandidate,
  SlotHold,
  TrackingKind,
  TrackingMap,
} from '../../types/taActionBoard';
import type { SlotHoldsState } from './useSlotHolds';

export function holdHandlers({
  slotHolds,
  tracking,
  setTracking,
  myEmail,
}: {
  slotHolds: SlotHoldsState;
  tracking: TrackingMap;
  setTracking: Dispatch<SetStateAction<TrackingMap>>;
  myEmail: string;
}) {
  return {
    onHolds: (role: RoleBoard, slots: GradedSlot[]) => {
      api
        .postHolds(role.postingId, slots)
        .then((r) => {
          slotHolds.apply(role.postingId, r.holds);
          // Say what was skipped: silently blocking fewer slots than were ticked
          // reads as a bug, and both reasons are the recruiter's to act on.
          const skipped = [
            r.skipped?.alreadyHeld ? `${r.skipped.alreadyHeld} already blocked` : null,
            r.skipped?.overCapacity ? `${r.skipped.overCapacity} over the 10-hold limit` : null,
          ].filter(Boolean);
          showToast({
            status: r.ok ? 'success' : 'warning',
            description: r.ok
              ? `${r.created} slot${r.created === 1 ? '' : 's'} blocked on the panel's calendars${
                  skipped.length > 0 ? ` (${skipped.join(', ')})` : ''
                }. Manage them under “Blocked slots”.`
              : // "Partially created" is only true when something WAS created —
                // saying it after a total failure tells a recruiter to go looking
                // for holds that do not exist. And the calendar's own reason
                // comes with it: without one, "1 calendar write failed" covers a
                // missing scope, a non-existent hold calendar and a bad attendee
                // address alike, which are three different fixes.
                `${
                  r.created > 0
                    ? `Holds partially created — ${r.failed.length} calendar write${
                        r.failed.length === 1 ? '' : 's'
                      } failed.`
                    : `No holds were created — ${r.failed.length} calendar write${
                        r.failed.length === 1 ? '' : 's'
                      } failed.`
                }${
                  r.error || r.failed[0]?.reason
                    ? ` Calendar said: ${r.error ?? r.failed[0]?.reason}`
                    : ''
                }`,
          });
        })
        .catch(() => showToast({ status: 'error', description: 'Could not create calendar holds.' }));
    },
    // Unticking a blocked slot: the confirmation already happened in the modal.
    onReleaseHold: (role: RoleBoard, hold: SlotHold) => {
      api
        .releaseHoldSlots({ postingId: role.postingId, starts: [hold.start] })
        .then((r) => {
          slotHolds.apply(role.postingId, r.holds);
          showToast({
            status: r.released > 0 ? 'success' : 'warning',
            description:
              r.released > 0
                ? 'Calendar hold released — the slot is free for anything else now.'
                : 'The hold could not be deleted from the calendar. It is still listed, so you can try again.',
          });
        })
        .catch(() => showToast({ status: 'error', description: 'Could not release the hold.' }));
    },
    onReleaseLegacyHolds: (role: RoleBoard) => {
      api
        .releaseHoldSlots({ postingId: role.postingId, legacy: true })
        .then((r) => {
          slotHolds.apply(role.postingId, r.holds);
          slotHolds.clearLegacy(role.postingId);
          showToast({
            status: 'success',
            description: `${r.released} hold${r.released === 1 ? '' : 's'} from the earlier version released.`,
          });
        })
        .catch(() => showToast({ status: 'error', description: 'Could not release those holds.' }));
    },
    /**
     * Withdraw the offered slot — a DECLINE, not a release.
     *
     * The candidate goes back to "ready to schedule" and their slots read free
     * to offer again, while every calendar hold stays put. A declined slot is
     * the likeliest one to re-offer, and dropping the hold at that moment is how
     * the time gets taken by an unrelated meeting before anyone re-offers it.
     * Releasing is a deliberate act in the panel section's overview.
     */
    onWithdraw: (candidate: SchedulingCandidate, role: RoleBoard) => {
      const prev = tracking;
      const entry = {
        kind: 'withdrawn' as TrackingKind,
        slot: null,
        note: null,
        at: new Date().toISOString(),
        by: myEmail,
      };
      setTracking({
        ...prev,
        [candidate.opportunityId]: {
          entries: [...(prev[candidate.opportunityId]?.entries || []), entry],
          status: 'ready to schedule',
        },
      });
      api
        .declineOffer({ postingId: role.postingId, opportunityId: candidate.opportunityId })
        .then((r) => {
          slotHolds.apply(role.postingId, r.holds);
          showToast({
            status: 'success',
            // Say only what happened. Claiming a release that did not occur is
            // what made the tracking table untrustworthy in the first place.
            description: r.declined
              ? `Offer withdrawn — ${candidate.name} is ready to schedule again, and ${r.declined === 1 ? 'their slot is' : `their ${r.declined} slots are`} free to offer again. The calendar hold${r.declined === 1 ? '' : 's'} stay${r.declined === 1 ? 's' : ''} until you release ${r.declined === 1 ? 'it' : 'them'} under “Blocked slots”.`
              : `Offer withdrawn — ${candidate.name} is ready to schedule again.`,
          });
        })
        .catch(() => {
          setTracking(prev);
          showToast({ status: 'error', description: 'Could not withdraw the offer.' });
        });
    },
  };
}
